import axios, { AxiosRequestConfig } from "axios";
import { RootURL } from "utils/Globals";
import { makeAutoObservable } from "mobx";
import urljoin from "url-join";

export type BaseResponse = {
  success?: boolean;
  message?: string;
  errors?: string[];
};

export type HttpOptions = {
  url: string;
  data?: any;
  onUploadProgress?: (percentage: number) => void;
  useFormData?: boolean;
  waitMessage?: string;
};

interface HttpRequest {
  id: number;
  url: string;
  waitMessage: string;
}

axios.defaults.headers["Pragma"] = "no-cache";

class Http {
  constructor() {
    makeAutoObservable(this);
  }

  //TODO: Move this request tracking stuff to a separate service so Http can be reused?
  nextId: number = 1;
  activeRequests: HttpRequest[] = [];

  get isFetchingData(): boolean {
    return this.activeRequests.length > 0;
  }
  get waitMessage(): string {
    if (this.activeRequests.length) {
      return this.activeRequests[this.activeRequests.length - 1].waitMessage;
    }

    return "";
  }
  addRequest(url: string, waitMessage: string): number {
    if (!waitMessage) return 0;
    const req: HttpRequest = { url, waitMessage, id: this.nextId };
    this.nextId += 1;
    this.activeRequests.push(req);
    return req.id;
  }

  removeRequest(id: number) {
    if (id <= 0) return;

    let index = this.activeRequests.findIndex((r) => r.id === id);
    if (index >= 0) {
      this.activeRequests.splice(index, 1);
    }
  }
  //end HttpRequest tracking code

  async get<T>(options: HttpOptions): Promise<T & BaseResponse> {
    const url = urljoin(RootURL, options.url);
    let requestId: any;
    try {
      requestId = this.addRequest(url, options.waitMessage || "");
      const res = await axios.get(url, { params: options.data || {} });
      this.removeRequest(requestId);

      return res.data as T;
    } finally {
      this.removeRequest(requestId);
    }
  }

  async post<T>(options: HttpOptions): Promise<T & BaseResponse> {
    let config: AxiosRequestConfig = {};

    //if we get an upload progress callback, attach it to axios config
    const onUploadProgressCallback = options.onUploadProgress;
    if (onUploadProgressCallback) {
      config.onUploadProgress = function (progressEvent: any) {
        const percentage = (progressEvent.loaded * 100) / progressEvent.total;
        onUploadProgressCallback(percentage);
      };
    }

    let postData: any = options.data || {};

    //build url with the RootURL
    const url = urljoin(RootURL, options.url);

    let requestId = this.addRequest(url, options.waitMessage || "");
    const res = await axios.post(url, postData, config);
    this.removeRequest(requestId);

    return res.data as T;
  }

  openNewTab(partialUrl: string) {
    const url = this.resolveClientUrlWithoutSessionToken(partialUrl);
    this.open(url, "_blank");
  }

  getRootUrlWithoutSessionToken() {
    try {
      const urlParts = new URL(RootURL);
      const pathName = urlParts.pathname.split("/", 2).pop();

      return `${urlParts.origin}/${pathName}/`;
    } catch {
      return "";
    }
  }

  resolveClientUrl(partialUrl: string) {
    return RootURL + partialUrl;
  }

  resolveClientUrlWithoutSessionToken(partialUrl: string) {
    return this.getRootUrlWithoutSessionToken() + partialUrl;
  }

  open(url: string, target?: string) {
    window.open(url, target || "_self");
  }
}

export default new Http();
